import { useState } from "react";
import type { ReactNode } from "react";

interface Props {
  title: string;
  tone?: "normal" | "danger";
  /** When set, Confirm stays disabled until this checkbox is ticked. */
  requireAck?: string;
  confirmLabel: string;
  busy: boolean;
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** The consequences. Rendering them is the whole point of this component. */
  children: ReactNode;
}

/**
 * Inline expanding confirmation, not a portal dialog.
 *
 * A dialog would need a focus trap, Escape handling, scroll lock, `aria-modal`
 * and a focus-return contract — hand-rolled a11y with no headless-UI dependency —
 * while the payloads here are multi-row tables (a pack plan, a consent's terms)
 * that a dialog cramps. `window.confirm` is excluded outright: it cannot render
 * the consequences, and rendering them is the reason this exists.
 */
export default function ConfirmPanel({
  title,
  tone = "normal",
  requireAck,
  confirmLabel,
  busy,
  error,
  onConfirm,
  onCancel,
  children,
}: Props) {
  const [acked, setAcked] = useState(false);
  const blocked = busy || (!!requireAck && !acked);
  const border = tone === "danger" ? "border-amber-500/50" : "border-kw-border-strong";

  return (
    <div className={`border ${border} rounded p-4 space-y-3 bg-kw-bg-overlay`}>
      <div className="text-sm text-kw-text-primary">{title}</div>
      <div className="text-xs text-kw-text-muted space-y-2">{children}</div>
      {requireAck && (
        <label className="flex items-start gap-2 text-xs text-kw-text-primary">
          <input
            type="checkbox"
            checked={acked}
            disabled={busy}
            onChange={(e) => setAcked(e.target.checked)}
            className="accent-kw-l1 mt-0.5"
          />
          <span>{requireAck}</span>
        </label>
      )}
      <div className="flex items-center gap-3 text-xs">
        <button
          onClick={onConfirm}
          disabled={blocked}
          className={`px-3 py-1.5 rounded disabled:opacity-50 ${
            tone === "danger" ? "bg-amber-500 text-black" : "bg-kw-l1 text-black"
          }`}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          className="text-kw-text-muted hover:text-kw-text-primary disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
      {error && <div className="text-xs text-red-400">{error}</div>}
    </div>
  );
}
